import { ChevronLeft, ChevronRight, FileText, Loader2 } from 'lucide-react'
import mammoth from 'mammoth'
import { lazy, Suspense, useEffect, useState } from 'react'
import { previewBlob } from '@/components/documents/document-preview-source'
import { PdfPageStack } from '@/components/documents/PdfPageStack'
import { AudioPlayer } from '@/components/ui/audio-player'
import { Skeleton } from '@/components/ui/skeleton'
import { useIsMobile } from '@/hooks/use-mobile'
import { apiClient } from '@/services/api/client'
import type { LakeDocument } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { isAudioFileType } from '@/utils/audio-playback'
import { parseCsvPreview } from '@/utils/csv-preview'

// XLSX preview is code-split (SheetJS ~1 MB) — loaded only when an xlsx/xls
// document is opened, not on app boot.
const XlsxPreview = lazy(() => import('@/components/documents/XlsxPreview'))

const XLSX_EXTS = ['xlsx', 'xls']
const PREVIEWABLE_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'csv', 'txt', 'docx', ...XLSX_EXTS]
const hasPreview = (fileType: string | null | undefined): boolean =>
  PREVIEWABLE_EXTS.includes(fileType || '') || isAudioFileType(fileType)

// Cap the raw CSV parse so a giant export can't freeze the tab; the full,
// queryable data lives in the Indexed Data tab's Parquet preview.
const CSV_PREVIEW_MAX_ROWS = 10_000
const CSV_ROWS_PER_PAGE = 100
// Cap the raw text preview (CDR logs can be 100+ MB) — a conservative slice
// keeps the DOM manageable; a "truncated" hint shows when content was clipped.
const TXT_PREVIEW_MAX_CHARS = 500_000
const TXT_PREVIEW_MAX_LINES = 5_000

interface LakeDocumentPreviewProps {
  document: LakeDocument
  initialPage?: number
}

export function LakeDocumentPreview({ document, initialPage }: LakeDocumentPreviewProps) {
  // Has settled by the time the file has loaded, so the phone never mounts an iframe.
  const isMobile = useIsMobile()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [csvRows, setCsvRows] = useState<string[][]>([])
  const [csvTruncated, setCsvTruncated] = useState(false)
  const [csvPage, setCsvPage] = useState(0)
  const [docxHtml, setDocxHtml] = useState<string | null>(null)
  const [xlsxBlob, setXlsxBlob] = useState<Blob | null>(null)
  const [textPreview, setTextPreview] = useState<{
    content: string
    totalLines: number
    truncated: boolean
  } | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null

    const loadDocument = async () => {
      try {
        setLoading(true)
        setError(null)
        setCsvRows([])
        setCsvTruncated(false)
        setCsvPage(0)
        setDocxHtml(null)
        setXlsxBlob(null)
        setTextPreview(null)

        const token = apiClient.getToken()

        // For DOCX with a converted PDF available, fetch the PDF preview instead
        // of the original DOCX file so we can render it identically to a native PDF.
        const fetchUrl =
          document.file_type === 'docx' && document.has_pdf_preview
            ? lakeApi.getDocxPdfPreviewUrl(document.id)
            : lakeApi.getFileUrl(document.s3_key)

        const response = await fetch(fetchUrl, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.statusText}`)
        }

        const blob = await response.blob()

        // The files route answers every file as `application/octet-stream`: a PDF (native,
        // or the one converted from a DOCX) and an audio file are typed here.
        const showsPdf =
          document.file_type === 'pdf' ||
          (document.file_type === 'docx' && document.has_pdf_preview)
        const blobWithType = previewBlob(
          blob,
          showsPdf ? 'pdf' : document.file_type,
          document.s3_key
        )

        objectUrl = URL.createObjectURL(blobWithType)
        setDocumentUrl(objectUrl)

        // Convert DOCX to HTML via mammoth — only when no converted PDF exists yet
        if (document.file_type === 'docx' && !document.has_pdf_preview) {
          const arrayBuffer = await blob.arrayBuffer()
          const result = await mammoth.convertToHtml({ arrayBuffer })
          setDocxHtml(result.value)
        }

        // Parse CSV for table preview — uses a single auto-detected
        // delimiter (`;` / `,` / `\t` / `|`) and a quote-aware splitter so
        // European-formatted numbers like `0,46326759` stay in one cell.
        if (document.file_type === 'csv') {
          const text = await blobWithType.text()
          const rows = parseCsvPreview(text, CSV_PREVIEW_MAX_ROWS)
          setCsvRows(rows)
          setCsvTruncated(rows.length >= CSV_PREVIEW_MAX_ROWS)
        }

        // Hand the raw blob to the lazy XlsxPreview (SheetJS) — it parses
        // once its chunk has loaded.
        if (XLSX_EXTS.includes(document.file_type || '')) {
          setXlsxBlob(blob)
        }

        // TXT — render the actual file content (the analysis only stores a few
        // sample_lines, and structured/log files may carry none). Read from the
        // already-downloaded blob and cap the slice for very large logs.
        if (document.file_type === 'txt') {
          const fullText = await blobWithType.text()
          const totalLines = fullText.split('\n').length
          const charClipped = fullText.length > TXT_PREVIEW_MAX_CHARS
          const sliced = charClipped ? fullText.slice(0, TXT_PREVIEW_MAX_CHARS) : fullText
          const lines = sliced.split('\n')
          const lineClipped = lines.length > TXT_PREVIEW_MAX_LINES
          const content = lineClipped ? lines.slice(0, TXT_PREVIEW_MAX_LINES).join('\n') : sliced
          setTextPreview({ content, totalLines, truncated: charClipped || lineClipped })
        }

        setLoading(false)
      } catch (err) {
        console.error('Failed to load document:', err)
        setError(err instanceof Error ? err.message : 'Failed to load document')
        setLoading(false)
      }
    }

    loadDocument()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [document.id, document.s3_key, document.file_type, document.has_pdf_preview])

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-destructive-border bg-destructive-surface p-4 text-sm text-destructive-foreground">
          {error}
        </div>
      </div>
    )
  }

  // A DOCX with a converted PDF is fetched and shown as that PDF.
  const showsPdf =
    document.file_type === 'pdf' || (document.file_type === 'docx' && document.has_pdf_preview)

  return (
    <div className="p-0 md:p-4">
      {/* Phone browsers cannot show a PDF inside an iframe, so the pages are drawn here. */}
      {documentUrl && isMobile && showsPdf && (
        <PdfPageStack documentUrl={documentUrl} className="h-[60dvh]" />
      )}

      {/* PDF */}
      {documentUrl && !isMobile && document.file_type === 'pdf' && (
        <iframe
          src={documentUrl + (initialPage ? `#page=${initialPage}` : '')}
          className="w-full h-[600px] border border-border rounded"
          title="Document preview"
        />
      )}

      {/* DOCX — converted PDF rendered identically to a native PDF */}
      {documentUrl && !isMobile && document.file_type === 'docx' && document.has_pdf_preview && (
        <iframe
          src={documentUrl}
          className="w-full h-[600px] border border-border rounded"
          title="Document preview"
        />
      )}

      {/* Images */}
      {documentUrl && ['jpg', 'jpeg', 'png', 'webp'].includes(document.file_type || '') && (
        <img
          src={documentUrl}
          alt={document.filename}
          className="max-w-full h-auto border border-border rounded"
        />
      )}

      {/* Audio — custom liquid-glass player. Uses the authenticated blob URL
          because the /api/v1/files/{s3_key} endpoint needs a bearer token in
          the Authorization header, which browsers do NOT send on bare media
          element src requests. */}
      {documentUrl && isAudioFileType(document.file_type) && (
        <AudioPlayer src={documentUrl} label={`Audio preview of ${document.filename}`} />
      )}

      {/* CSV — raw rows shown as-is (preamble included), paginated. No header
          detection: this is the verbatim file, not the processed table. */}
      {document.file_type === 'csv' && csvRows.length > 0 && (
        <CsvTablePreview
          rows={csvRows}
          page={csvPage}
          onPageChange={setCsvPage}
          truncated={csvTruncated}
        />
      )}

      {/* XLSX — actual spreadsheet via SheetJS (all sheets). */}
      {XLSX_EXTS.includes(document.file_type || '') && xlsxBlob && (
        <Suspense
          fallback={
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading spreadsheet…</span>
            </div>
          }
        >
          <XlsxPreview blob={xlsxBlob} filename={document.filename} />
        </Suspense>
      )}

      {/* TXT — actual file content, verbatim (scroll to page). Capped for very
          large logs; the full queryable data lives in the Indexed Data tab. */}
      {document.file_type === 'txt' && textPreview && (
        <div className="border border-border rounded overflow-auto max-h-[600px]">
          <pre className="font-mono text-xs text-foreground whitespace-pre p-3 leading-relaxed">
            {textPreview.content}
          </pre>
          {textPreview.truncated && (
            <div className="text-xs text-muted-foreground text-center py-2 italic border-t border-border bg-muted/40 sticky bottom-0">
              Preview truncated · showing first {TXT_PREVIEW_MAX_LINES.toLocaleString()} lines of{' '}
              {textPreview.totalLines.toLocaleString()} total
            </div>
          )}
        </div>
      )}

      {/* DOCX — mammoth HTML fallback when converted PDF not yet available */}
      {document.file_type === 'docx' && !document.has_pdf_preview && docxHtml && (
        <div
          className="border border-border rounded p-4 overflow-auto max-h-[600px] prose prose-sm prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      )}

      {/* DOCX — waiting state: PDF being generated, mammoth also failed */}
      {document.file_type === 'docx' && !document.has_pdf_preview && !docxHtml && !loading && (
        <div className="border border-border rounded p-6 text-center text-muted-foreground text-sm">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Loader2 className="h-5 w-5 animate-spin opacity-60" />
            <FileText className="h-5 w-5 opacity-40" />
          </div>
          <p>PDF preview is being generated during processing.</p>
          <p className="text-xs mt-2 text-muted-foreground/70">
            Check back once phase 1 indexing completes.
          </p>
        </div>
      )}

      {/* Unsupported or no preview */}
      {!hasPreview(document.file_type) && (
        <div className="border border-border rounded p-6 text-center text-muted-foreground text-sm">
          <p>Preview not available for this file type.</p>
          <p className="text-xs mt-2 text-muted-foreground/70">
            {document.filename} ({(document.file_size / 1024).toFixed(1)} KB)
          </p>
        </div>
      )}
    </div>
  )
}

function CsvTablePreview({
  rows,
  page,
  onPageChange,
  truncated,
}: {
  rows: string[][]
  page: number
  onPageChange: (page: number) => void
  truncated: boolean
}) {
  const pageCount = Math.max(1, Math.ceil(rows.length / CSV_ROWS_PER_PAGE))
  const safePage = Math.min(page, pageCount - 1)
  const start = safePage * CSV_ROWS_PER_PAGE
  const pageRows = rows.slice(start, start + CSV_ROWS_PER_PAGE)
  const end = Math.min(start + CSV_ROWS_PER_PAGE, rows.length)

  return (
    <div className="space-y-2">
      <div className="border border-border rounded overflow-auto max-h-[600px]">
        <table className="w-full text-xs">
          <tbody>
            {pageRows.map((row, i) => {
              const absoluteIndex = start + i
              return (
                <tr key={absoluteIndex} className="align-top">
                  <td className="border-b border-border px-2 py-1.5 text-right text-muted-foreground select-none tabular-nums w-12">
                    {absoluteIndex + 1}
                  </td>
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className="border-b border-border px-3 py-1.5 text-foreground whitespace-pre"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Rows {rows.length === 0 ? 0 : start + 1}–{end} of {rows.length.toLocaleString()}
          {truncated ? '+ (preview capped)' : ''}
        </span>
        {pageCount > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded border border-border px-2 py-1 text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={safePage === 0}
              onClick={() => onPageChange(Math.max(0, safePage - 1))}
              aria-label="Previous rows"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span>
              Page {safePage + 1} of {pageCount}
            </span>
            <button
              type="button"
              className="inline-flex items-center rounded border border-border px-2 py-1 text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={safePage >= pageCount - 1}
              onClick={() => onPageChange(Math.min(pageCount - 1, safePage + 1))}
              aria-label="Next rows"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
